/**
 * Task Form State Utilities
 *
 * Shared utilities for ComprehensiveTaskForm's clone, template, and LLM prefill features.
 * Provides:
 * - TaskFormState: serializable snapshot of all 60+ form fields
 * - collectFormState / applyFormState: serialize/deserialize to individual setters
 * - BUILTIN_SCENARIOS: 5 ready-made task templates (extracted from Demo)
 * - TASK_FIELD_MANIFEST: per-field metadata for LLM prefill prompt construction
 */

import type { Task, EscalationRule } from '../data/repositories/TaskRepository';

// ============================================================================
// TaskFormState — serializable snapshot of all form fields
// Dates stored as ISO strings, JSON fields as strings, everything flat.
// ============================================================================

export interface DualControlState {
  required: boolean;
  firstSigner?: string;
  firstSignerName?: string;
  firstSignedAt?: string;
  firstSignerMfaVerified: boolean;
  secondSigner?: string;
  secondSignedAt?: string;
  secondSignerMfaVerified: boolean;
}

export interface ApprovalDecision {
  approverId: string;
  approverName: string;
  decision: 'approved' | 'rejected' | 'revision_requested';
  reasonCode?: string;
  comments?: string;
  timestamp?: string;
}

export interface SubTask {
  id: string;
  name: string;
  taskType: string;
  priority: string;
  assignedTo?: string;
  estimatedDuration?: number;
  sequenceOrder: number;
}

export interface TaskFormState {
  // Core Identity
  name: string;
  description: string;
  externalRef: string;
  taskCategory: string;
  domain: string;
  // Classification
  taskType: string;
  tags: string[];
  // Status & Lifecycle
  priority: string;
  dueDate: string | null;
  deadlineAt: string | null;
  estimatedDuration: number | undefined;
  statusReason: string;
  blockedReason: string;
  // Assignment
  assignedTo: string;
  assignedTeam: string;
  assignedRole: string;
  assignmentMethod: 'manual' | 'auto' | 'escalated';
  watchers: string[];
  collaborators: string[];
  delegates: string[];
  // Urgency
  priorityScore: number;
  priorityReason: string;
  isCritical: boolean;
  requiresImmediateAttention: boolean;
  // Dependencies & Hierarchy
  dependencies: string[];
  relatedTasks: string[];
  isParent: boolean;
  childTasks: SubTask[];
  completionStrategy: string;
  // Form & Data
  formSchema: string;
  formVersion: string;
  formTemplateId: string;
  contextData: string;
  variables: string;
  // Approval & Security
  approvalRequired: boolean;
  approvalType: string;
  approvalPolicyId: string;
  approvers: string[];
  approvalDecisions: ApprovalDecision[];
  dualControl: DualControlState;
  requiresMfa: boolean;
  securityLevel: string;
  accessControlList: string[];
  requiredPermissions: string[];
  requiredRoles: string[];
  // Escalation & SLA
  escalationRules: EscalationRule[];
  escalationChain: string[];
  slaDeadline: string | null;
  slaBufferMinutes: number;
  notificationChannels: string[];
  // Integration
  integrationId: string;
  webhookUrl: string;
  callbackUrl: string;
  isSystemTask: boolean;
  isAutomated: boolean;
  automationScript: string;
  maxRetries: number;
  retryPolicy: string;
  // UI & Display
  uiComponent: string;
  icon: string;
  color: string;
  displayOrder: number | undefined;
  renderMode: string;
  allowComments: boolean;
  allowAttachments: boolean;
  allowDelegation: boolean;
  isArchived: boolean;
  // Compliance
  complianceFrameworks: string[];
  regulatoryAlignmentPct: number | undefined;
  regulatoryRequirements: string[];
  evidenceRequired: boolean;
  isConfidential: boolean;
  isPrivileged: boolean;
  retentionPeriodDays: number | undefined;
  legalHold: boolean;
  // Metadata & Extensibility
  customFields: string;
  labels: Record<string, string>;
  locale: string;
  timezone: string;
  // Collaboration
  draftMode: boolean;
  feedbackScore: number | undefined;
}

// ============================================================================
// Setter interface — maps each TaskFormState key to its setter function
// ============================================================================

export type TaskFormSetters = {
  [K in keyof TaskFormState]: (value: TaskFormState[K]) => void;
};

// ============================================================================
// applyFormState — distribute a (possibly partial) state to individual setters
// ============================================================================

export function applyFormState(
  state: Partial<TaskFormState>,
  setters: Partial<TaskFormSetters>,
): void {
  for (const key of Object.keys(state) as (keyof TaskFormState)[]) {
    const setter = setters[key];
    if (setter && state[key] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (setter as any)(state[key]);
    }
  }
}

// ============================================================================
// serializeForInitialData — convert TaskFormState to Partial<Task>
// ============================================================================

export function serializeForInitialData(state: TaskFormState): Partial<Task> & Record<string, unknown> {
  return {
    name: state.name,
    description: state.description,
    taskType: state.taskType,
    priority: state.priority,
    assignedTo: state.assignedTo,
    assignedTeam: state.assignedTeam,
    dependencies: state.dependencies,
    approvalRequired: state.approvalRequired,
    // Top-level fields the Demo puts outside metadata
    tags: state.tags,
    domain: state.domain,
    taskCategory: state.taskCategory,
    assignedRole: state.assignedRole,
    estimatedDuration: state.estimatedDuration,
    dueDate: state.dueDate ?? undefined,
    deadlineAt: state.deadlineAt ?? undefined,
    metadata: {
      externalRef: state.externalRef,
      taskCategory: state.taskCategory,
      domain: state.domain,
      tags: state.tags,
      statusReason: state.statusReason,
      blockedReason: state.blockedReason,
      assignedRole: state.assignedRole,
      assignmentMethod: state.assignmentMethod,
      watchers: state.watchers,
      collaborators: state.collaborators,
      delegates: state.delegates,
      priorityScore: state.priorityScore,
      priorityReason: state.priorityReason,
      isCritical: state.isCritical,
      requiresImmediateAttention: state.requiresImmediateAttention,
      relatedTasks: state.relatedTasks,
      isParent: state.isParent,
      completionStrategy: state.completionStrategy,
      formSchema: state.formSchema,
      formVersion: state.formVersion,
      formTemplateId: state.formTemplateId,
      contextData: state.contextData,
      variables: state.variables,
      approvalType: state.approvalType,
      approvalPolicyId: state.approvalPolicyId,
      approvers: state.approvers,
      approvalDecisions: state.approvalDecisions,
      dualControl: state.dualControl,
      requiresMfa: state.requiresMfa,
      securityLevel: state.securityLevel,
      accessControlList: state.accessControlList,
      requiredPermissions: state.requiredPermissions,
      requiredRoles: state.requiredRoles,
      escalationRules: state.escalationRules,
      escalationChain: state.escalationChain,
      slaDeadline: state.slaDeadline ?? undefined,
      slaBufferMinutes: state.slaBufferMinutes,
      notificationChannels: state.notificationChannels,
      deadlineAt: state.deadlineAt ?? undefined,
      integrationId: state.integrationId,
      webhookUrl: state.webhookUrl,
      callbackUrl: state.callbackUrl,
      isSystemTask: state.isSystemTask,
      isAutomated: state.isAutomated,
      automationScript: state.automationScript,
      maxRetries: state.maxRetries,
      retryPolicy: state.retryPolicy,
      uiComponent: state.uiComponent,
      icon: state.icon,
      color: state.color,
      displayOrder: state.displayOrder,
      renderMode: state.renderMode,
      allowComments: state.allowComments,
      allowAttachments: state.allowAttachments,
      allowDelegation: state.allowDelegation,
      isArchived: state.isArchived,
      complianceFrameworks: state.complianceFrameworks,
      regulatoryAlignmentPct: state.regulatoryAlignmentPct,
      regulatoryRequirements: state.regulatoryRequirements,
      evidenceRequired: state.evidenceRequired,
      isConfidential: state.isConfidential,
      isPrivileged: state.isPrivileged,
      retentionPeriodDays: state.retentionPeriodDays,
      legalHold: state.legalHold,
      customFields: state.customFields,
      labels: state.labels,
      locale: state.locale,
      timezone: state.timezone,
      draftMode: state.draftMode,
      feedbackScore: state.feedbackScore,
    },
  } as any;
}

// ============================================================================
// TASK_FIELD_MANIFEST — per-field metadata for LLM prefill prompt construction
// ============================================================================

export type FieldManifestEntry = {
  name: keyof TaskFormState;
  tab: 'core' | 'hierarchy' | 'security' | 'escalation' | 'compliance' | 'advanced';
  type: 'string' | 'number' | 'boolean' | 'string[]' | 'date' | 'json' | 'object' | 'select';
  description: string;
  pii?: boolean;
  safetyCritical?: boolean;
  options?: string[];
};

export const TASK_FIELD_MANIFEST: FieldManifestEntry[] = [
  // Core tab
  { name: 'name', tab: 'core', type: 'string', description: 'Task name/title' },
  { name: 'description', tab: 'core', type: 'string', description: 'Detailed description' },
  { name: 'taskType', tab: 'core', type: 'select', description: 'Type of task', options: ['user', 'system', 'approval', 'review', 'manual', 'validation'] },
  { name: 'taskCategory', tab: 'core', type: 'select', description: 'Business category', options: ['registration', 'coding', 'billing', 'clinical', 'administrative', 'compliance'] },
  { name: 'domain', tab: 'core', type: 'string', description: 'Business domain (healthcare, finance, legal, etc.)' },
  { name: 'tags', tab: 'core', type: 'string[]', description: 'Descriptive tags' },
  { name: 'priority', tab: 'core', type: 'select', description: 'Priority level', options: ['low', 'normal', 'high', 'urgent', 'critical'] },
  { name: 'priorityScore', tab: 'core', type: 'number', description: 'Priority score 0-100' },
  { name: 'priorityReason', tab: 'core', type: 'string', description: 'Reason for priority level' },
  { name: 'isCritical', tab: 'core', type: 'boolean', description: 'Life-critical flag', safetyCritical: true },
  { name: 'requiresImmediateAttention', tab: 'core', type: 'boolean', description: 'Needs immediate attention' },
  { name: 'estimatedDuration', tab: 'core', type: 'number', description: 'Estimated duration in minutes' },
  { name: 'assignedTo', tab: 'core', type: 'string', description: 'Assigned user ID', pii: true },
  { name: 'assignedTeam', tab: 'core', type: 'string', description: 'Assigned team' },
  { name: 'assignedRole', tab: 'core', type: 'string', description: 'Required role' },
  // Hierarchy tab
  { name: 'isParent', tab: 'hierarchy', type: 'boolean', description: 'Is this a parent task with children' },
  { name: 'completionStrategy', tab: 'hierarchy', type: 'select', description: 'Child task completion strategy', options: ['all_children_must_complete', 'any_child_completes', 'percentage_threshold', 'weighted_completion'] },
  // Security tab
  { name: 'approvalRequired', tab: 'security', type: 'boolean', description: 'Requires approval before execution', safetyCritical: true },
  { name: 'approvalType', tab: 'security', type: 'select', description: 'Type of approval needed', options: ['manager', 'compliance', 'medical', 'financial', 'legal', 'multi_level'] },
  { name: 'approvers', tab: 'security', type: 'string[]', description: 'List of approver user IDs', pii: true },
  { name: 'requiresMfa', tab: 'security', type: 'boolean', description: 'MFA required for submission', safetyCritical: true },
  { name: 'securityLevel', tab: 'security', type: 'select', description: 'Data classification level', options: ['public', 'internal', 'confidential', 'secret', 'top_secret'] },
  { name: 'requiredPermissions', tab: 'security', type: 'string[]', description: 'Required permissions' },
  { name: 'requiredRoles', tab: 'security', type: 'string[]', description: 'Required roles for dual control' },
  // Escalation tab
  { name: 'notificationChannels', tab: 'escalation', type: 'string[]', description: 'Notification delivery channels', options: ['email', 'sms', 'push', 'slack', 'webhook'] },
  { name: 'slaBufferMinutes', tab: 'escalation', type: 'number', description: 'SLA buffer time in minutes' },
  // Compliance tab
  { name: 'complianceFrameworks', tab: 'compliance', type: 'string[]', description: 'Applicable compliance frameworks', options: ['HIPAA', 'SOX', 'GDPR', 'FDA_21_CFR_PART_11', 'ISO_27001', 'PCI_DSS', 'FINRA'], safetyCritical: true },
  { name: 'evidenceRequired', tab: 'compliance', type: 'boolean', description: 'Requires evidence collection' },
  { name: 'isConfidential', tab: 'compliance', type: 'boolean', description: 'Contains confidential data' },
  { name: 'retentionPeriodDays', tab: 'compliance', type: 'number', description: 'Data retention period in days' },
  // Advanced tab
  { name: 'isAutomated', tab: 'advanced', type: 'boolean', description: 'Automated system task' },
  { name: 'automationScript', tab: 'advanced', type: 'string', description: 'Automation script code' },
  { name: 'webhookUrl', tab: 'advanced', type: 'string', description: 'Webhook callback URL' },
  { name: 'locale', tab: 'advanced', type: 'string', description: 'Locale code (e.g., en-US)' },
  { name: 'timezone', tab: 'advanced', type: 'string', description: 'Timezone identifier' },
];

// ============================================================================
// BUILTIN_SCENARIOS — 5 ready-made task templates (extracted from Demo)
// ============================================================================

export interface BuiltinScenario {
  key: string;
  title: string;
  description: string;
  color: string;
  features: string[];
  mode: 'create';
  instanceId: string;
  showLifeCriticalFeatures: boolean;
  initialData: Partial<Task> & Record<string, unknown>;
}

/**
 * @deprecated Use `useTaskTemplates().builtinTemplates` instead — these are now seeded
 * in DynamoDB via seed configs and served from the backend.
 * Kept temporarily as fallback data when backend is unavailable.
 */
export const BUILTIN_SCENARIOS: BuiltinScenario[] = [
  {
    key: 'healthcare',
    title: 'Life-Critical Healthcare',
    description: 'High-risk medication administration with dual control + MFA',
    color: 'red',
    features: ['Dual control', 'MFA required', 'HIPAA & FDA compliance', 'Evidence collection'],
    mode: 'create',
    instanceId: 'workflow-patient-admission-001',
    showLifeCriticalFeatures: true,
    initialData: {
      name: 'Medication Administration - High-Risk Drug',
      description: 'Administer chemotherapy medication to patient. Requires dual control and MFA verification due to life-critical nature.',
      taskType: 'manual',
      priority: 'critical',
      tags: ['life-critical', 'chemotherapy', 'dual-control-required', 'mfa-required'],
      domain: 'healthcare',
      taskCategory: 'clinical',
      assignedTeam: 'oncology-nurses',
      assignedRole: 'registered_nurse',
      estimatedDuration: 30,
      dueDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      deadlineAt: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
      approvalRequired: true,
      metadata: {
        isCritical: true,
        requiresImmediateAttention: false,
        priorityScore: 95,
        priorityReason: 'High-risk chemotherapy administration requires maximum safety protocols',
        dualControl: { required: true, firstSignerMfaVerified: false, secondSignerMfaVerified: false },
        requiresMfa: true,
        securityLevel: 'confidential',
        approvalType: 'medical',
        approvers: ['dr-smith-oncologist', 'dr-jones-chief-medical'],
        requiredRoles: ['registered_nurse', 'oncology_certified'],
        requiredPermissions: ['medication:administer:high_risk', 'patient:access:protected'],
        complianceFrameworks: ['HIPAA', 'FDA_21_CFR_PART_11'],
        regulatoryRequirements: ['Two-person verification required', 'MFA authentication mandatory', 'Complete audit trail', 'Evidence collection required'],
        evidenceRequired: true,
        isConfidential: true,
        retentionPeriodDays: 2555,
        notificationChannels: ['email', 'sms', 'push'],
        contextData: JSON.stringify({ patientId: 'PT-2024-00123', patientName: 'John Doe', medicationName: 'Carboplatin', dosage: '450mg', route: 'IV', allergies: ['penicillin'], criticalAlerts: ['Monitor for anaphylaxis'] }, null, 2),
        formSchema: JSON.stringify({ type: 'object', required: ['verificationCompleted', 'vitalSigns', 'patientConsent'], properties: { verificationCompleted: { type: 'boolean', title: 'Five Rights Verification Complete' }, vitalSigns: { type: 'object', properties: { bloodPressure: { type: 'string' }, heartRate: { type: 'number' }, temperature: { type: 'number' } } }, patientConsent: { type: 'boolean', title: 'Patient Verbal Consent Obtained' }, adverseReactions: { type: 'string', title: 'Any Adverse Reactions Observed' } } }, null, 2),
      },
    } as any,
  },
  {
    key: 'financial',
    title: 'Financial Approval (SOX)',
    description: 'Large wire transfer requiring multi-level approval',
    color: 'green',
    features: ['SOX compliance', 'Dual control', 'Escalation chain', '7-year retention'],
    mode: 'create',
    instanceId: 'workflow-wire-transfer-002',
    showLifeCriticalFeatures: true,
    initialData: {
      name: 'Approve Wire Transfer - $250,000',
      description: 'Multi-level approval required for large wire transfer. SOX compliance requires dual control and complete audit trail.',
      taskType: 'approval',
      priority: 'urgent',
      tags: ['financial', 'dual-control', 'sox-compliance', 'wire-transfer'],
      domain: 'finance',
      taskCategory: 'financial',
      assignedTo: 'cfo@company.com',
      assignedRole: 'chief_financial_officer',
      estimatedDuration: 15,
      dueDate: new Date(Date.now() + 1 * 60 * 60 * 1000).toISOString(),
      deadlineAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      approvalRequired: true,
      metadata: {
        isCritical: false,
        requiresImmediateAttention: true,
        priorityScore: 85,
        priorityReason: 'Large financial transaction requires urgent approval',
        dualControl: { required: true, firstSignerMfaVerified: false, secondSignerMfaVerified: false },
        requiresMfa: true,
        securityLevel: 'secret',
        approvalType: 'financial',
        approvers: ['cfo@company.com', 'ceo@company.com'],
        requiredRoles: ['chief_financial_officer', 'treasurer'],
        requiredPermissions: ['finance:approve:wire_transfer', 'finance:dual_control'],
        complianceFrameworks: ['SOX', 'FINRA'],
        regulatoryRequirements: ['Dual control required for amounts > $100,000', 'Complete audit trail', 'MFA required for all signers', 'Segregation of duties enforced'],
        evidenceRequired: true,
        retentionPeriodDays: 2555,
        notificationChannels: ['email', 'sms'],
        escalationRules: [
          { id: 'rule-1', trigger: 'overdue', condition: {}, action: 'escalate_priority', delayMinutes: 30 },
          { id: 'rule-2', trigger: 'overdue', condition: {}, action: 'reassign', escalateTo: 'board-of-directors@company.com', delayMinutes: 60 },
        ],
        escalationChain: ['cfo@company.com', 'ceo@company.com', 'board@company.com'],
        contextData: JSON.stringify({ transactionId: 'TXN-2024-05678', amount: 250000, currency: 'USD', beneficiary: 'Vendor ABC Inc.', beneficiaryAccount: '****1234', purpose: 'Equipment Purchase' }, null, 2),
      },
    } as any,
  },
  {
    key: 'hierarchy',
    title: 'Complex Parent Task',
    description: 'Patient discharge with multiple nested subtasks',
    color: 'blue',
    features: ['Parent-child relationships', 'Multiple subtasks', 'Completion strategy'],
    mode: 'create',
    instanceId: 'workflow-patient-discharge-003',
    showLifeCriticalFeatures: false,
    initialData: {
      name: 'Complete Patient Discharge Process',
      description: 'Multi-step patient discharge workflow with nested subtasks. All children must complete before patient can be discharged.',
      taskType: 'user',
      priority: 'high',
      tags: ['patient-discharge', 'workflow', 'multi-step'],
      domain: 'healthcare',
      taskCategory: 'administrative',
      assignedTeam: 'discharge-planning',
      estimatedDuration: 120,
      dueDate: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
      metadata: {
        isParent: true,
        completionStrategy: 'all_children_must_complete',
        priorityScore: 70,
        complianceFrameworks: ['HIPAA'],
        contextData: JSON.stringify({ patientId: 'PT-2024-00456', patientName: 'Jane Smith', admissionDate: '2024-10-01', dischargeDestination: 'Home with Home Health', primaryDiagnosis: 'Pneumonia' }, null, 2),
      },
    } as any,
  },
  {
    key: 'escalation',
    title: 'Escalation & SLA',
    description: 'Customer complaint with automatic escalation',
    color: 'orange',
    features: ['4 escalation rules', 'Auto-escalate', 'SLA monitoring', 'Multi-channel notifications'],
    mode: 'create',
    instanceId: 'workflow-customer-complaint-004',
    showLifeCriticalFeatures: false,
    initialData: {
      name: 'Resolve Critical Customer Complaint',
      description: 'High-priority customer complaint with strict SLA. Auto-escalates if not resolved within timeframe.',
      taskType: 'user',
      priority: 'urgent',
      tags: ['customer-service', 'escalation', 'sla-monitored'],
      domain: 'customer-service',
      taskCategory: 'complaint',
      assignedTo: 'support-tier2@company.com',
      estimatedDuration: 60,
      dueDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
      metadata: {
        priorityScore: 90,
        requiresImmediateAttention: true,
        escalationRules: [
          { id: 'rule-1', trigger: 'overdue', condition: {}, action: 'notify', escalateTo: 'supervisor@company.com', delayMinutes: 30 },
          { id: 'rule-2', trigger: 'overdue', condition: {}, action: 'reassign', escalateTo: 'manager@company.com', delayMinutes: 60 },
          { id: 'rule-3', trigger: 'overdue', condition: {}, action: 'escalate_priority', delayMinutes: 90 },
          { id: 'rule-4', trigger: 'stale', condition: { inactiveHours: 2 }, action: 'notify', escalateTo: 'on-call@company.com', delayMinutes: 120 },
        ],
        escalationChain: ['support-tier2@company.com', 'supervisor@company.com', 'manager@company.com', 'director@company.com'],
        slaDeadline: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
        slaBufferMinutes: 30,
        notificationChannels: ['email', 'sms', 'slack'],
        contextData: JSON.stringify({ customerId: 'CUST-12345', customerName: 'Acme Corporation', complaintType: 'Service Outage', severity: 'Critical', ticketId: 'TKT-2024-9876', impactedUsers: 150, businessImpact: 'Production system down - revenue loss estimated at $10k/hour' }, null, 2),
      },
    } as any,
  },
  {
    key: 'automation',
    title: 'Automated Integration',
    description: 'System task with retry logic and webhooks',
    color: 'violet',
    features: ['Automated execution', 'Retry with backoff', 'Webhook notifications', 'External API'],
    mode: 'create',
    instanceId: 'workflow-data-sync-005',
    showLifeCriticalFeatures: false,
    initialData: {
      name: 'Sync Patient Data to External System',
      description: 'Automated task to sync patient data with external EHR system. Includes retry logic and webhook notifications.',
      taskType: 'system',
      priority: 'normal',
      tags: ['automation', 'integration', 'ehr-sync'],
      domain: 'healthcare',
      taskCategory: 'integration',
      assignedTo: 'system-integration-service',
      estimatedDuration: 5,
      metadata: {
        isSystemTask: true,
        isAutomated: true,
        automationScript: `// Automated EHR sync script
async function syncPatientData(context) {
  const { patientId, ehrSystemUrl, apiKey } = context;
  const patientData = await fetchPatientData(patientId);
  const response = await fetch(ehrSystemUrl + '/api/patients', {
    method: 'POST',
    headers: { 'Authorization': 'Bearer ' + apiKey, 'Content-Type': 'application/json' },
    body: JSON.stringify(patientData)
  });
  if (!response.ok) throw new Error('EHR sync failed: ' + response.statusText);
  return { success: true, syncedAt: new Date().toISOString() };
}`,
        integrationId: 'integration-ehr-epic-001',
        webhookUrl: 'https://api.example.com/webhooks/task-completed',
        callbackUrl: 'https://api.example.com/callbacks/sync-result',
        maxRetries: 5,
        retryPolicy: JSON.stringify({ maxAttempts: 5, initialBackoffMs: 1000, backoffMultiplier: 2.0, maxBackoffMs: 60000, retryOnErrors: ['ECONNREFUSED', 'ETIMEDOUT', '502', '503', '504'] }, null, 2),
        notificationChannels: ['webhook', 'email'],
        complianceFrameworks: ['HIPAA'],
        contextData: JSON.stringify({ patientId: 'PT-2024-00789', ehrSystemUrl: 'https://ehr.example.com', syncDirection: 'outbound', dataTypes: ['demographics', 'vitals', 'medications', 'allergies'] }, null, 2),
      },
    } as any,
  },
];

